import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Section } from "@/components/Section";
import { STUDENT_WINS, STATS } from "@/lib/constants";
import { Trophy, Play } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const WinCard = ({ photo, idx }) => (
  <motion.figure
    whileHover={{ scale: 1.035, rotate: idx % 2 === 0 ? -0.6 : 0.6 }}
    transition={{ duration: 0.35, ease: EASE }}
    className="relative shrink-0 w-44 sm:w-52 md:w-56 aspect-[4/5] rounded-2xl overflow-hidden border border-maple-border bg-maple-cream2 shadow-[0_10px_30px_-12px_rgba(31,45,36,0.35)]"
    data-testid={`student-win-${idx}`}
  >
    <img
      src={photo.src}
      alt={photo.alt}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-contain bg-maple-cream transition-transform duration-[1400ms] ease-out hover:scale-[1.04]"
      onError={(e) => {
        // Graceful fallback so the layout never breaks before photos are dropped in.
        e.currentTarget.style.display = "none";
        const fallback = e.currentTarget.nextElementSibling;
        if (fallback) fallback.style.display = "flex";
      }}
    />
    <div
      className="absolute inset-0 hidden items-center justify-center text-maple-navy/40 font-serif text-sm"
      aria-hidden="true"
    >
      Photo {String(idx + 1).padStart(2, "0")}
    </div>

    {/* Soft bottom wash so the trophy badge reads on any photo */}
    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-maple-navy/55 to-transparent" />

    {/* Pass stamp */}
    <div className="absolute top-3 left-3 rounded-full bg-maple-cream2/95 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-maple-navy backdrop-blur">
      Passed
    </div>

    {/* Bottom trophy badge */}
    <figcaption className="absolute inset-x-0 bottom-0 p-3 sm:p-4">
      <div className="flex items-end justify-end">
        <span className="wins-stamp inline-flex h-9 w-9 items-center justify-center rounded-full bg-maple-tan2 text-maple-cream2 shadow-md">
          <Trophy className="h-4 w-4" aria-hidden="true" />
        </span>
      </div>
    </figcaption>
  </motion.figure>
);

const VideoCard = ({ idx, src, onPlayingChange }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const handlePlay = () => {
    videoRef.current?.play();
  };

  const setPlayState = (next) => {
    setPlaying(next);
    onPlayingChange?.(next);
  };

  return (
    <figure
      className="relative shrink-0 w-44 sm:w-52 md:w-56 aspect-[4/5] rounded-2xl overflow-hidden border border-maple-border bg-maple-navy shadow-[0_10px_30px_-12px_rgba(31,45,36,0.35)]"
      data-testid={`student-video-${idx}`}
    >
      <video
        ref={videoRef}
        src={src}
        controls={playing}
        playsInline
        preload="metadata"
        onPlay={() => setPlayState(true)}
        onPause={() => setPlayState(false)}
        onEnded={() => setPlayState(false)}
        className="absolute inset-0 h-full w-full object-contain bg-maple-navy"
      />
      {!playing && (
        <button
          type="button"
          onClick={handlePlay}
          className="absolute inset-0 flex items-center justify-center bg-maple-navy/35 hover:bg-maple-navy/25 transition-colors"
          aria-label="Play student review video"
        >
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-maple-cream2/95 text-maple-navy shadow-lg">
            <Play className="h-6 w-6 fill-maple-navy translate-x-[1px]" aria-hidden="true" />
          </span>
        </button>
      )}
      <div className="pointer-events-none absolute top-3 left-3 rounded-full bg-maple-cream2/95 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-maple-navy backdrop-blur">
        Passed
      </div>
    </figure>
  );
};

const MarqueeRow = ({ items, reverse = false, startIndex = 0 }) => {
  // Duplicate the list so the loop is seamless (track shifts by -50%).
  const doubled = [...items, ...items];
  // When any embedded video is playing, pause the entire marquee.
  const [playingCount, setPlayingCount] = useState(0);
  const handlePlayingChange = (isPlaying) => {
    setPlayingCount((c) => Math.max(0, c + (isPlaying ? 1 : -1)));
  };
  const paused = playingCount > 0;

  return (
    <div className="wins-marquee py-2" data-testid={`wins-marquee-${reverse ? "right" : "left"}`}>
      <div className={`wins-track ${reverse ? "reverse" : ""} ${paused ? "paused" : ""}`}>
        {doubled.map((item, i) =>
          item.type === "video" ? (
            <VideoCard
              key={`video-${i}`}
              idx={startIndex + i}
              src={item.src}
              onPlayingChange={handlePlayingChange}
            />
          ) : (
            <WinCard key={`${item.src}-${i}`} photo={item} idx={startIndex + i} />
          )
        )}
      </div>
    </div>
  );
};

// Sprinkle videos through the photo marquee so they surface as users scroll.
const marqueeItems = (() => {
  const total = STUDENT_WINS.length;
  const firstSpot = Math.floor(total / 3);
  const secondSpot = Math.floor((total * 2) / 3);
  return [
    ...STUDENT_WINS.slice(0, firstSpot),
    { type: "video", src: "/Review_1.mp4" },
    ...STUDENT_WINS.slice(firstSpot, secondSpot),
    { type: "video", src: "/Review_2.mp4" },
    ...STUDENT_WINS.slice(secondSpot),
  ];
})();

export const StudentWins = () => {
  return (
    <Section id="wins" testId="section-wins" className="bg-maple-cream2/40">
      {/* Stats strip — sits right above the photos */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.55, ease: EASE }}
        className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 rounded-3xl glass-card p-4 sm:p-5"
        data-testid="wins-stats"
      >
        {STATS.map((s, i) => (
          <div
            key={i}
            className="flex flex-col items-start sm:items-center p-3 sm:p-4 text-left sm:text-center"
            data-testid={`wins-stat-${i}`}
          >
            <span className="font-serif text-3xl sm:text-4xl text-maple-navy tracking-tight">
              {s.value}
            </span>
            <span className="mt-1 text-xs sm:text-sm uppercase tracking-[0.12em] text-maple-brown">
              {s.label}
            </span>
          </div>
        ))}
      </motion.div>

      {/* Single marquee row with mixed photos + featured video */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6, ease: EASE }}
        className="mt-8 sm:mt-10"
      >
        <MarqueeRow items={marqueeItems} startIndex={0} />
      </motion.div>

    </Section>
  );
};
